import axios from 'axios';
import { CLEAR_AUTH_EVENT } from '../constants/authEvents';

// Detect API URL
export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;
  if (process.env.NODE_ENV === 'production') return `${window.location.origin}/api`;
  return 'http://localhost:3001/api';
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  // No default Content-Type here — axios already sets application/json for
  // plain object payloads on its own. Forcing it as an instance default was
  // breaking every FormData upload (avatar, ID document, property images):
  // axios only skips JSON-encoding a FormData body when Content-Type isn't
  // already application/json, so multer's routes always saw an empty
  // request and returned "No file uploaded".
  // withCredentials sends the httpOnly auth_token cookie on every request.
  // The server sets this cookie on login/register; never stored in localStorage.
  withCredentials: true,
  // Prevents a stalled request (common on slow mobile data) from spinning forever.
  // Individual calls (e.g. large image uploads) can override this per-request.
  timeout: 30000,
});

/**
 * Clear any legacy localStorage remnants and notify AuthContext.
 * Called on 401/403 with an auth-invalid code so the UI drops to the
 * unauthenticated state without a full-page reload.
 */
export function clearClientAuthAndNotify() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.dispatchEvent(new Event(CLEAR_AUTH_EVENT));
}

/**
 * POST /auth/refresh using the httpOnly refresh cookie — needs no existing
 * token, works purely off the (now shared-domain) refresh cookie. Returns
 * the fresh access token on success, null otherwise. Shared by the response
 * interceptor's silent-refresh-and-retry below and by AuthContext's
 * mount-time session hydration (recognising a session cookie set by logging
 * in on a different *.dwello.ikxano.com app).
 */
export async function attemptSilentRefresh(): Promise<string | null> {
  try {
    const { data } = await axios.post(`${getApiBaseUrl()}/auth/refresh`, {}, { withCredentials: true });
    const newToken: string | undefined = data?.data?.token;
    if (!newToken) return null;
    localStorage.setItem('token', newToken);
    api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
    return newToken;
  } catch {
    return null;
  }
}

/** Auth headers for fetch-based callers (e.g. insights SSE) that bypass axios. */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

// ── Request interceptor ──────────────────────────────────────────────────────
// Send httpOnly cookie (withCredentials) and Bearer token from localStorage.
// Web login stores the JWT in both places; either may be valid depending on
// cookie expiry, cross-origin setup, or proxy configuration.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor — silent token refresh ───────────────────────────────
// On 401/403 with an auth-invalid code, attempt one silent POST /auth/refresh
// (the httpOnly refresh_token cookie is sent automatically via withCredentials).
// If the refresh succeeds, store the new access token and retry the original
// request. If it fails, clear auth state and redirect to login.

let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

function processQueue(newToken: string) {
  refreshQueue.forEach(cb => cb(newToken));
  refreshQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const authInvalid =
      code === 'INVALID_TOKEN' ||
      code === 'TOKEN_EXPIRED' ||
      code === 'AUTH_ERROR';

    if ((status === 401 || status === 403) && authInvalid) {
      const originalRequest = error.config;

      // A request that never carried a token wasn't an expired session to
      // begin with — it's a component making an authenticated call for a
      // guest (a bug in that component, not a session problem). Don't nuke
      // the whole page with a silent-refresh-then-redirect cascade for it;
      // just let that one call fail so the component handles it locally.
      // (This is what let ReviewContext's unconditional /reviews/user call
      // hard-redirect anonymous visitors on public pages like /property/:id
      // straight to /login — fixed at the source, this is the backstop.)
      if (!originalRequest.headers?.Authorization) {
        return Promise.reject(error);
      }

      // Avoid infinite loop if the refresh call itself fails
      if (originalRequest._retried) {
        clearClientAuthAndNotify();
        const path = window.location.pathname;
        const publicLike =
          path === '/' ||
          path === '/properties' ||
          path === '/login' ||
          path === '/register' ||
          path === '/forgot-password' ||
          path.startsWith('/reset-password');
        if (!publicLike) window.location.assign('/login');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue concurrent requests while a refresh is in flight
        return new Promise<string>(resolve => {
          refreshQueue.push(resolve);
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retried = true;
      isRefreshing = true;

      try {
        const newToken = await attemptSilentRefresh();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(newToken);
          return api(originalRequest);
        }
        throw new Error('No token in refresh response');
      } catch {
        refreshQueue = [];
        clearClientAuthAndNotify();
        const path = window.location.pathname;
        const publicLike =
          path === '/' ||
          path === '/properties' ||
          path === '/login' ||
          path === '/register' ||
          path === '/forgot-password' ||
          path.startsWith('/reset-password');
        if (!publicLike) window.location.assign('/login');
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
